import { useMemo } from 'react'

import { matchFilterNumber, matchFilterString, sortArrayBy } from '@domain-group/javascript-utils'

import { useLoadMore, useWatchEntityUpdates } from '@domain-group/react-hooks'

import useReduxAction from '@hooks/useReduxAction'
import useSelector from '@hooks/useSelector'

import type { CampaignQuoteItemModel, CampaignQuoteItemRequestOptions } from '@models/types'
import type { ModuleState } from '@redux/modules/types'

const watchEntityKeys = ['campaignQuoteItems']

type CampaignQuoteItemFilters = {
  campaign_quote_id?: number,
  campaign_quote_section_id?: number,
  selected?: 'true' | 'false',
  string?: string,
}

type UseCampaignQuoteItemsOptions = {
  filters?: CampaignQuoteItemFilters,
  performHttpRequests?: boolean,
  requestOptions?: CampaignQuoteItemRequestOptions,
}

function useCampaignQuoteItems(options: UseCampaignQuoteItemsOptions) {
  const { filters = {}, requestOptions } = options
  const {
    campaign_quote_id: filterCampaignQuoteId,
    campaign_quote_section_id: filterCampaignQuoteSectionId,
    selected: filterSelected,
    string: filterString,
  } = filters

  const {
    updatedEntities: { campaignQuoteItems: campaignQuoteItemsUpdatedAt },
  } = useWatchEntityUpdates(watchEntityKeys)

  const { campaignQuoteItems } = useSelector(reduxState => reduxState.entities)

  const filteredCampaignQuoteItems = useMemo(() => {
    const filtered = Object.values(campaignQuoteItems).filter((campaignQuoteItem: CampaignQuoteItemModel) => {
      const {
        campaign_quote_id, campaign_quote_section_id, id, selected, title,
      } = campaignQuoteItem

      const matchId = id.toString() === filterString
      const matchTitle = matchFilterString(title, filterString)
      const matchString = matchId || matchTitle

      const matchSelected = matchFilterString(selected.toString(), filterSelected, { exact: true })

      const matchCampaignQuoteId = matchFilterNumber(campaign_quote_id, filterCampaignQuoteId)
      const matchCampaignQuoteSectionId = matchFilterNumber(campaign_quote_section_id, filterCampaignQuoteSectionId)

      return matchCampaignQuoteId && matchCampaignQuoteSectionId && matchSelected && matchString
    })

    return sortArrayBy(filtered, 'asc', 'sort')
  }, [campaignQuoteItemsUpdatedAt, JSON.stringify(filters)])

  const filteredCampaignQuoteItemsCount = filteredCampaignQuoteItems.length
  const hasFilteredCampaignQuoteItems = !!filteredCampaignQuoteItemsCount

  const loadMorePayload = useLoadMore({
    filters,
    loadedCount: filteredCampaignQuoteItemsCount,
    performHttpRequests: options.performHttpRequests,
  })

  const {
    callbacks: { loadMore },
    canLoadMore,
    filtersWithOffset,
    limit,
    performHttpRequests,
  } = loadMorePayload

  const { loading: loadingCampaignQuoteItems } = useReduxAction(
    'campaignQuoteItems',
    'loadCampaignQuoteItems',
    {
      ...requestOptions,
      ...filtersWithOffset,
      limit,
    },
    [filtersWithOffset, performHttpRequests],
    {
      shouldPerformFn: ({ loading }: ModuleState) => performHttpRequests && !loading,
    },
  )

  return {
    callbacks: {
      loadMore,
    },
    canLoadMore,
    filteredCampaignQuoteItems,
    filteredCampaignQuoteItemsCount,
    hasFilteredCampaignQuoteItems,
    loading: loadingCampaignQuoteItems,
  }
}

export default useCampaignQuoteItems
